import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Send, Loader2, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", occasion: "", rating: 5, quote: "" };
const inputCls =
    "w-full rounded-xl border border-[#FFFDF9]/15 bg-[#FFFDF9]/5 px-4 py-3 text-sm text-[#FFFDF9] placeholder:text-[#FFFDF9]/35 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/60 transition-[box-shadow,border-color] duration-300";

export default function ReviewForm() {
    const [form, setForm] = useState(initial);
    const [hover, setHover] = useState(0);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/reviews`, form);
            setDone(true);
            toast.success("Thank you! Your review was sent to Annabell.");
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(Array.isArray(d) ? "Please write at least a sentence or two." : "Something went wrong — please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="rounded-[2rem] border border-[#D4AF37]/30 bg-[#FFFDF9]/5 p-8 text-center" data-testid="review-success">
                <Heart className="mx-auto h-8 w-8 fill-[#C95B7D] text-[#C95B7D]" />
                <p className="mt-3 font-display text-xl font-bold text-[#F8D7E3]">Thank you so much!</p>
                <p className="mt-1 text-sm text-[#FFFDF9]/70">Annabell reads every review personally — yours will appear here once she's had a look.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="review-form" className="rounded-[2rem] border border-[#FFFDF9]/10 bg-[#FFFDF9]/[0.04] p-6 sm:p-8">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">Leave a Review</p>
            <h3 className="mt-2 font-display text-xl sm:text-2xl font-bold text-[#FFFDF9]">Ordered from Annabell? Tell us how it went.</h3>

            <div className="mt-5 flex items-center gap-1" data-testid="review-rating" role="radiogroup" aria-label="Rating">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={form.rating === n}
                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                        data-testid={`review-star-${n}`}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setForm({ ...form, rating: n })}
                        className="p-0.5 transition-transform duration-200 hover:scale-110"
                    >
                        <Star className={`h-7 w-7 transition-colors ${(hover || form.rating) >= n ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#FFFDF9]/25"}`} />
                    </button>
                ))}
                <span className="ml-2 text-xs text-[#FFFDF9]/60">{form.rating} / 5</span>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <input required maxLength={80} data-testid="review-name-input" value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} />
                <input maxLength={120} data-testid="review-occasion-input" value={form.occasion} onChange={set("occasion")} placeholder="What did you order? (optional)" className={inputCls} />
                <textarea
                    required
                    minLength={10}
                    maxLength={1000}
                    rows={4}
                    data-testid="review-quote-input"
                    value={form.quote}
                    onChange={set("quote")}
                    placeholder="Tell future customers about your treats… *"
                    className={`${inputCls} sm:col-span-2`}
                />
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-[#FFFDF9]/45">Reviews are read by Annabell before they appear on the site.</p>
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="review-submit-button"
                    className="group inline-flex items-center gap-2 rounded-full bg-[#C95B7D] px-6 py-3 text-sm font-semibold text-white hover:bg-[#b44a6b] disabled:opacity-60 transition-colors"
                >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                    {loading ? "Sending…" : "Send Review"}
                </button>
            </div>
        </form>
    );
}
